import { getStatusErrorMessaging } from './api-error';
import { HttpVerb } from './call-endpoint';
import { CallFormResult } from './call-form-endpoint';

export interface UploadFileOptions {
  body: FormData;
  clientToken: string;
  onProgress?: (progress: number) => void;
  method: HttpVerb;
  url: string;
  headers?: Headers;
  errorOn404?: boolean;
}

export async function uploadFile<T = void>({
  body,
  clientToken,
  onProgress,
  method,
  url,
  headers,
  errorOn404,
}: UploadFileOptions): Promise<{
  promise: Promise<void | T>;
  abort: () => void;
}> {
  const controller = new AbortController();

  const promise = new Promise<T | void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percentComplete = (event.loaded / event.total) * 100;
        onProgress(percentComplete);
      }
    });

    xhr.addEventListener('load', async () => {
      if (controller.signal.aborted) {
        return;
      }

      const response = new Response(xhr.responseText, {
        status: xhr.status,
        statusText: xhr.statusText,
      });

      const json = response.status !== 404 && response.status !== 413 ? await response.json() : undefined;

      if (
        !response.ok &&
        (response.status === 413 || response.status !== 404 || (response.status === 404 && errorOn404))
      ) {
        const result = json ? (json as CallFormResult) : undefined;
        reject(
          new Error(result ? result.context : response.statusText || getStatusErrorMessaging(response.status))
        );
      } else {
        resolve(json as T);
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network Error'));
    });

    controller.signal.addEventListener('abort', () => {
      console.log('Request aborted');
      reject(new Error('Request aborted'));
      xhr.abort();
    });

    xhr.open(method, url, true);

    xhr.setRequestHeader('Authorization', `Bearer ${clientToken}`);

    if (headers) {
      for (const [name, value] of headers.entries()) {
        xhr.setRequestHeader(name, value);
      }
    }

    xhr.send(body);
  });

  return { promise, abort: () => controller.abort() };
}
